import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import {
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant
} from '@flowwai-frontend/design-system'
import {
   ClockFastBackwardIcon,
   MessageTextSquare02Icon,
   PlusIcon
} from '@flowwai-frontend/design-system/icons'
import {
   ChatConfigUIStateEnum,
   useAIChatUIStore
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import { theme } from '../../theme'

interface Props {
   onClickHistoryInExpandedView: () => void
   onClickNewThreadButton: () => void
   threadName?: string
   isChatBoxExpanded: boolean
   shouldShowChatHistoryInSidepeek: boolean
   renderActionButtons: () => React.ReactElement
}

const ThreadHeader = (props: Props) => {
   const {
      onClickHistoryInExpandedView,
      onClickNewThreadButton,
      threadName,
      isChatBoxExpanded,
      shouldShowChatHistoryInSidepeek
   } = props

   const themes = theme.threadDetailedView
   const aiChatUIStore = useAIChatUIStore()

   const renderHeaderLeftActions = (): React.ReactElement => (
      <div className='flex items-center gap-x-2 mr-6'>
         <IconButton
            hierarchy={IconButtonHierarchy.Tertiary}
            size={'Small'}
            subVariant={IconButtonSubVariant.Gray}
            icon={() => (
               <ClockFastBackwardIcon
                  height={20}
                  width={20}
                  fill={themes.historyIconColor}
               />
            )}
            onClick={onClickHistoryInExpandedView}
         />
         <IconButton
            hierarchy={IconButtonHierarchy.Tertiary}
            size={'Small'}
            subVariant={IconButtonSubVariant.Gray}
            icon={() => (
               <PlusIcon height={20} width={20} fill={themes.plusIconColor} />
            )}
            onClick={() => {
               onClickNewThreadButton()
               aiChatUIStore.setActiveUIStateEnum(
                  ChatConfigUIStateEnum.SearchState
               )
            }}
         />
      </div>
   )

   const renderTitleWithActionButtons = () => {
      const headerText = threadName ?? 'Untitled'

      return (
         <div
            className={cn('justify-between items-center flex ', {
               'h-[64px]': isChatBoxExpanded
            })}
         >
            <div className='flex items-center truncate pl-[20px] pt-[20px] pb-2'>
               {isChatBoxExpanded && !shouldShowChatHistoryInSidepeek
                  ? renderHeaderLeftActions()
                  : null}
               <div className='flex gap-2 items-center truncate mr-2'>
                  <MessageTextSquare02Icon
                     width={18}
                     height={18}
                     className='shrink-0'
                  />
                  <p
                     className={cn(
                        'text-lg-semibold grow truncate',
                        themes.threadTitleColor
                     )}
                     title={headerText}
                  >
                     {headerText}
                  </p>
               </div>
            </div>
            <div className='pr-[20px] pt-[20px] pb-2'>
               {props.renderActionButtons()}
            </div>
         </div>
      )
   }

   return (
      <div className='flex flex-col gap-3'>
         {renderTitleWithActionButtons()}
      </div>
   )
}

export default observer(ThreadHeader)
